import { useState, useEffect, useCallback } from 'react';
import { Search, GripVertical, X, Plus, Save, Loader2, Package, Filter, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  adminGetCrossSell, adminUpdateCrossSell, adminGetAllCrossSell,
  adminGetProducts, adminGetProductVendors,
} from '@/lib/api';

const AdminCrossSell = () => {
  const [conditions, setConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [crossSellProducts, setCrossSellProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [overviewData, productsData, vendorsData] = await Promise.all([
          adminGetAllCrossSell(),
          adminGetProducts(),
          adminGetProductVendors(),
        ]);
        setConditions(overviewData?.conditions || []);
        setAllProducts(productsData || []);
        setVendors(vendorsData?.vendors || []);
      } catch { /* */ } finally { setLoading(false); }
    };
    load();
  }, []);

  const loadCrossSell = useCallback(async (conditionId) => {
    try {
      const data = await adminGetCrossSell(conditionId);
      setCrossSellProducts((data?.cross_sell_products || []).map(cs => ({
        ...cs.product,
        reason: cs.reason || '',
        cs_id: cs.id,
      })));
      setHasChanges(false);
    } catch {
      toast.error('Failed to load cross-sell products');
    }
  }, []);

  const selectCondition = (cond) => {
    setSelectedCondition(cond);
    loadCrossSell(cond.condition_id);
    setSearchQuery('');
  };

  const assignedIds = new Set(crossSellProducts.map(p => p.id));

  const filteredProducts = allProducts.filter(p => {
    if (assignedIds.has(p.id)) return false;
    if (!p.shopify_handle) return false;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (p.product_name || '').toLowerCase().includes(q) ||
      (p.vendor || '').toLowerCase().includes(q);
    const matchesVendor = !vendorFilter || (p.vendor || '') === vendorFilter;
    return matchesSearch && matchesVendor;
  });

  const addProduct = (product) => {
    setCrossSellProducts(prev => [...prev, { ...product, reason: '' }]);
    setHasChanges(true);
  };

  const removeProduct = (productId) => {
    setCrossSellProducts(prev => prev.filter(p => p.id !== productId));
    setHasChanges(true);
  };

  const updateReason = (productId, reason) => {
    setCrossSellProducts(prev => prev.map(p => p.id === productId ? { ...p, reason } : p));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedCondition) return;
    setSaving(true);
    try {
      const payload = crossSellProducts.map((p, i) => ({
        product_id: p.id,
        reason: p.reason || '',
        sort_order: i + 1,
        active: true,
      }));
      await adminUpdateCrossSell(selectedCondition.condition_id, payload);
      setHasChanges(false);
      toast.success('Cross-sell products saved');
      // Refresh overview counts
      const overview = await adminGetAllCrossSell();
      setConditions(overview?.conditions || []);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="cross-sell-manager">
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl text-[#1a1a1a] mb-1">Cross-Sell Products</h1>
            <p className="text-gray-500 text-sm">
              {selectedCondition
                ? `Managing: ${selectedCondition.condition_name}`
                : 'Select a condition to manage its complementary product recommendations'}
            </p>
          </div>
          {selectedCondition && (
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="btn-primary" data-testid="save-cross-sell-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {hasChanges ? 'Save Changes' : 'Saved'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Condition list */}
        <div className="w-[280px] border-r border-gray-200 overflow-y-auto bg-gray-50">
          <div className="p-3">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3 px-2">Conditions</p>
            {conditions.map(cond => (
              <button
                key={cond.condition_id}
                onClick={() => selectCondition(cond)}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left mb-1 transition-colors ${
                  selectedCondition?.condition_id === cond.condition_id
                    ? 'bg-[#1a1a1a] text-white'
                    : 'hover:bg-gray-100 text-[#1a1a1a]'
                }`}
                data-testid={`condition-${cond.condition_id}`}
              >
                <span className="text-sm truncate">{cond.condition_name}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${cond.cross_sell_count > 0 ? 'bg-[#d4e4bc] text-[#1a1a1a]' : 'bg-gray-200 text-gray-500'}`}>
                    {cond.cross_sell_count}
                  </Badge>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Middle: Assigned cross-sell products */}
        {selectedCondition ? (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">
                Cross-Sell Products ({crossSellProducts.length})
              </p>

              {crossSellProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No cross-sell products assigned yet</p>
                  <p className="text-xs mt-1">Search and add products from the catalog on the right</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {crossSellProducts.map((product, index) => (
                    <div key={product.id} className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg group" data-testid={`cross-sell-item-${product.id}`}>
                      <span className="text-xs text-gray-400 mt-3 w-5">{index + 1}</span>
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1a1a1a]">{product.product_name}</p>
                        {product.vendor && <p className="text-[10px] text-gray-400 uppercase">{product.vendor}</p>}
                        <Input
                          placeholder="Why this product? (shown to customer)"
                          value={product.reason || ''}
                          onChange={(e) => updateReason(product.id, e.target.value)}
                          className="mt-2 h-8 text-xs"
                          data-testid={`reason-${product.id}`}
                        />
                      </div>
                      {product.price > 0 && <span className="text-sm text-gray-500 flex-shrink-0">${product.price.toFixed(2)}</span>}
                      <button
                        onClick={() => removeProduct(product.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all mt-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product catalog */}
            <div className="w-[340px] border-l border-gray-200 flex flex-col bg-gray-50">
              <div className="p-4 border-b border-gray-200 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="cross-sell-search"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} className="text-xs text-gray-600">
                    <Filter className="w-3 h-3 mr-1" /> Filters
                  </Button>
                  <span className="text-xs text-gray-400">{filteredProducts.length} available</span>
                </div>
                {showFilters && (
                  <div className="flex flex-wrap gap-1">
                    <button onClick={() => setVendorFilter('')} className={`text-[10px] px-2 py-0.5 rounded-full ${!vendorFilter ? 'bg-[#1a1a1a] text-white' : 'bg-white border text-gray-600'}`}>All</button>
                    {vendors.map(v => (
                      <button key={v} onClick={() => setVendorFilter(vendorFilter === v ? '' : v)} className={`text-[10px] px-2 py-0.5 rounded-full ${vendorFilter === v ? 'bg-[#1a1a1a] text-white' : 'bg-white border text-gray-600'}`}>{v}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {filteredProducts.slice(0, 80).map(product => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product)}
                    className="w-full flex items-center gap-3 p-2.5 mb-1 bg-white rounded-lg border border-gray-100 text-left hover:border-gray-300 transition-colors"
                    data-testid={`catalog-cs-${product.id}`}
                  >
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0"><Package className="w-3 h-3 text-gray-300" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#1a1a1a] truncate">{product.product_name}</p>
                      <span className="text-[9px] text-gray-400 uppercase">{product.vendor}</span>
                    </div>
                    <Plus className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-sm">Select a condition from the left to manage its cross-sell products</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCrossSell;
